export interface InactiveIngestionInterval {
  start: number;
  end: number | null;
}

export interface CaptureClockSnapshot {
  captureState: "running" | "paused" | "stopped";
  captureStateChangedAt: number | null;
  captureClockStartedAt: number | null;
  captureClockInactiveIntervals?: readonly { start: number; end: number }[];
}

export function inactiveIntervalsForCaptureClock(
  clock: CaptureClockSnapshot,
): InactiveIngestionInterval[] {
  const intervals: InactiveIngestionInterval[] = (clock.captureClockInactiveIntervals ?? [])
    .filter(isValidClosedInterval)
    .map((interval) => ({ start: interval.start, end: interval.end }));

  if (
    clock.captureState !== "running" &&
    clock.captureClockStartedAt !== null &&
    clock.captureStateChangedAt !== null &&
    clock.captureStateChangedAt >= clock.captureClockStartedAt
  ) {
    intervals.push({ start: clock.captureStateChangedAt, end: null });
  }

  return intervals.sort((a, b) => a.start - b.start);
}

export function activeTimeForTimestamp(
  timestamp: number,
  inactiveIntervals: readonly InactiveIngestionInterval[],
): number {
  return timestamp - inactiveDurationBefore(timestamp, inactiveIntervals);
}

export function activeElapsedBetween(
  start: number,
  end: number,
  inactiveIntervals: readonly InactiveIngestionInterval[],
): number {
  return Math.max(
    0,
    activeTimeForTimestamp(end, inactiveIntervals) -
      activeTimeForTimestamp(start, inactiveIntervals),
  );
}

export function mapEntriesToActiveTime<T extends { ts: number }>(
  entries: readonly T[],
  inactiveIntervals: readonly InactiveIngestionInterval[],
): T[] {
  if (inactiveIntervals.length === 0) return [...entries];
  return entries.map((entry) => ({
    ...entry,
    ts: activeTimeForTimestamp(entry.ts, inactiveIntervals),
  }));
}

export function selectCaptureClockEntries<T extends { ts: number }>(
  entries: readonly T[],
  clock: CaptureClockSnapshot,
): T[] {
  const startedAt = clock.captureClockStartedAt;
  if (startedAt === null || !Number.isFinite(startedAt)) return [...entries];
  return entries.filter((entry) => entry.ts >= startedAt);
}

function inactiveDurationBefore(
  timestamp: number,
  inactiveIntervals: readonly InactiveIngestionInterval[],
): number {
  let total = 0;
  let coveredThrough = Number.NEGATIVE_INFINITY;
  const ordered = [...inactiveIntervals].sort((a, b) => a.start - b.start);

  for (const interval of ordered) {
    if (interval.start >= timestamp) break;
    const start = Math.max(interval.start, coveredThrough);
    const rawEnd = interval.end ?? timestamp;
    const end = Math.min(rawEnd, timestamp);
    if (end <= start) continue;
    total += end - start;
    coveredThrough = Math.max(coveredThrough, end);
  }

  return total;
}

function isValidClosedInterval(interval: { start: number; end: number }): boolean {
  return Number.isFinite(interval.start) &&
    Number.isFinite(interval.end) &&
    interval.end > interval.start;
}
